/* Dynamic Plot code copyright 2017 (c) Tuva Labs Inc. */
/**
 * Map plot element
 * Added to Dyn.PlotShaper's list of plot elements, to make a plot.
 * created by jpatel, extended by cmiller
 */

import Raphael from 'raphael';
import { BubbleHelper, DotPlotBase } from './dotPlotElement';
import L from 'leaflet'; /* leaflet library */
import 'leaflet/dist/leaflet.css';
import ContextMenuUtil from '../../../utils/ContextMenuUtil.js';
import { caseSizes } from "../../../utils/constants";

export const wrapDynMapElement = function(Dyn) {
	let mediator = Dyn.mediator;

/**
 * Dyn.MapElement()
 * Add map layer in the plotview.
 * Uses leaflet, OpenStreetMap
 */
class MapElement extends DotPlotBase {

    /**
     * @param Dyn
     * @param args
     * @param args.plotContainer -- Used to capture the plotContainer reference (HTML element)
     */
    constructor( Dyn, args ) {
        super( Dyn ); // MUST call constructor of class it extends (super-class)
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityShape, //kPriorityMap
            replaceType: Dyn.EPlotControl.kControlsShape, //kControlsMap
            primaryType: Dyn.EPlotControl.kControlsMap,
            otherControls: [
                Dyn.EPlotControl.kControlsCaseOrder, // if we sort cases, or allow user-sort
                //Dyn.EPlotControl.kControlsMargins // so that calcAxis() is called
            ]
        }, Dyn );

        // coordinates we set map to:
        // 'lat' == latitude - vertical geographic coordinate (Y-axis)
        // 'lng' == longitude - horizontal geographic coordinate (X-axis)
        // NOTE: leaflet maps have fixed zoom levels in X and Y, and thus will show a much
        // larger map area, in order to include our 'desired' Lat and Lng. See kZoomDelta, kZoomSnap.
        this.desiredLat = new Dyn.mathUtil.Range( MapElement.kWorldLat.min, MapElement.kWorldLat.max );
        this.desiredLng = new Dyn.mathUtil.Range( MapElement.kWorldLng.min, MapElement.kWorldLng.max );

        // pixBounds - boundaries of map or containing view rectangles
        this.desiredPixBounds = new Dyn.mathUtil.Rect();

        // adjustments for div alignment; div id='plotContainer' has padding, contains svg and '.leaflet-map-pane'
        let plotContainer = args && args.plotContainer;
        this.containerToSvgOffsetX = parseFloat( $( plotContainer ).css( 'padding-left' ) ) || 0;
        this.containerToSvgOffsetY = parseFloat( $( plotContainer ).css( 'padding-top' ) ) || 0;
        // NOTE: container no longer has this padding, save for future reference --CDM 2020-07-20
        Dyn.assert( this.containerToSvgOffsetX === 0, "Map plot cannot get container padding" );

        this.leafletMap = null; // our leafletjs.com map object
        this.dragButton = null;
        this.selectButton = null;
        this.resetButton = null;
        this.isMappingX = false; // true if mappable longitude coordinates on X axes
        this.isMappingY = false; // true if mappable latitutde coordinates on Y axes
        this.isInDragMode = true; // true if in drag mode, false if in marquee select mode
        this.needsFitToData = true; // if true, on next plot refresh, use fitMapToDataLatLng()

        this.DEBUG_showMarkers = false;
    }

    get kDefaultTitle() {
        return Dyn.plotTitles.MAP_PLOT;
    }

    get kUndoAction() {
        return 'Map';
    }

    // static constants
    static kZoomDelta = 0.25; // +/- zoom buttons change zoom this much [0-1]
    static kZoomSnap = 0.25; // zoom level rounds to this fraction [0-1]
    static kLatValidRange = new Dyn.mathUtil.Range( -90, 90 ); // strict
    static kLngValidRange = new Dyn.mathUtil.Range( -400, 400 ); // loose, ok to wrap
    static kWorldLat = new Dyn.mathUtil.Range( -55, 55 ); // approximate range of initial world map on X axis
    static kWorldLng = new Dyn.mathUtil.Range( -5, 5 ); // approximate range of initial world map on Y axis
    static kMapMaxBounds = [ [ 90, 360 ], [ -90, -360 ] ]; // hard maximum bounds in Leaflet setBounds format (http://leafletjs.com/reference-1.2.0.html#map-setmaxbounds)

    /** Destroy a Dyn.PlotElement */
    destroy() {

        // destroy our map object
        if( this.leafletMap ) {
            this.leafletMap.eachLayer( function( layer ) {
                layer.remove();
            } );
            this.leafletMap.remove();
            this.leafletMap = null;
        }

        super.destroy();
        Dyn.assert( this.plotShaper === null, "Dyn.PlotElement.destroy was not called" );
    }

    /** save the map for later restore() */
    save() {
        let plotElementModel = super.save(); // call inherited save()

        plotElementModel.primaryType = this.primaryType;
        plotElementModel.latLngPoints = [
            this.desiredLat.min, this.desiredLat.max,
            this.desiredLng.min, this.desiredLng.max
        ];
        if( this.leafletMap ) {
            // save the current center of the map
            // NOTE: this is redundant with plotElementModel.latLngPoints; consider removing it --CDM 2023-10-23
            let latLng = this.leafletMap.getCenter();
            plotElementModel.centerLatLng = { lat: latLng.lat, lng: latLng.lng };
        }
        plotElementModel.isInDragMode = this.isInDragMode;

        return plotElementModel;
    }

    /** Restore the map */
    restore( savedData ) {
        //console.log("MapElement.restore old Lng,Lat", this.desiredLng, this.desiredLat );
        super.restore( savedData ); // call inherited restore()

        // restore the latLanPoints
        if( savedData.latLngPoints && savedData.latLngPoints.length ) {
            this.desiredLat = new Dyn.mathUtil.Range( savedData.latLngPoints[0], savedData.latLngPoints[1] );
            this.desiredLng = new Dyn.mathUtil.Range( savedData.latLngPoints[2], savedData.latLngPoints[3] );
            this.needsRestoreLatLng = true;
        }
        // restore the map center
        if( savedData.centerLatLng ) {
            this.needThisCenterLatLng = savedData.centerLatLng;
        }
        // restore the drag mode or not. Plot view will check this and restore view mode at appropriate time)
        if( typeof savedData.isInDragMode === "boolean" ) { // do nothing if undefined, otherwise set true or false
            this.isInDragMode = savedData.isInDragMode;
        }
        //console.log("MapElement.restore new Lng,Lat", this.desiredLng, this.desiredLat, this.needThisCenterLatLng );
    }

    /** Return plot summary for map plot to enable accessibility for the tools */
    getPlotSummary( options ) {
        if( options.wantPrimary ) {
            return Dyn.stringFormat( Dyn.plotUtil.mapPlotSummary, this.plotShaper.dataSet.getNumCasesPreFilter() );
        }
    }

    /** Update the plot's axis model, removing any elements incompatible with this PlotElement. */
    calcAxis( ioWhatChanged, subViewRef ) {
        //console.log( "Map calcAxis()");
        var self = this,
            xAxisView = subViewRef.xAxisView,
            yAxisView = subViewRef.yAxisView,
            plotView = Dyn.plotView,
            plotElementIDinDOM = plotView.getPlotElementId();

        this.wantStackUp = this.calcStackDirection( xAxisView, yAxisView ); // not used; but keep base class happy

        // use horizontal orientation except when the existing axes have numeric on Y but not X.
        this.isMappingX = xAxisView.isNumericActiveOrInactive();
        this.isMappingY = yAxisView.isNumericActiveOrInactive();
        if( ioWhatChanged.attributeReset ) {
            // clear any user-specified and plot-specified axis settings
            // so that the axis range defaults to the attribute data range
            xAxisView.setPlotSpecificMinMax();
            yAxisView.setPlotSpecificMinMax();
            xAxisView.setUserCustomizedMinMax();
            yAxisView.setUserCustomizedMinMax();
            this.needsFitToData = true; // re-fit the map to data
        }

        // update default titles once on plot creation, then let user customize (don't change on recalc)
        this.plotShaper.setDefaultPlotTitle( subViewRef.topMargin, Dyn.loc( this.kDefaultTitle ) );
        if( xAxisView.isEmptyOfAttributes() ) {
            xAxisView.setTitle( Dyn.loc( Dyn.plotStrings.promptMapX ) );
        }
        if( yAxisView.isEmptyOfAttributes() ) {
            yAxisView.setTitle( Dyn.loc( Dyn.plotStrings.promptMapY ) );
        }

        // create map layer and set up callbacks
        if( !this.leafletMap ) {
            this.leafletMap = new L.Map( plotElementIDinDOM, {
                zoomControl: true, // VISIBLE + - zoom controls, which call MapElement.onZoomEnd()
                zoomDelta: MapElement.kZoomDelta, // see https://leafletjs.com/examples/zoom-levels/
                zoomSnap: MapElement.kZoomSnap,
                dragging: this.isInDragMode, // user selectable, see makeToolButtons()
                // disable other map internation controls
                touchZoom: false,
                scrollWheelZoom: false,
                doubleClickZoom: false,
                tap: false,
                // max bounds
                maxBounds: MapElement.kMapMaxBounds, // Set a maximum bounds, to avoid infinite wrapping in longitude (east-west)
                maxBoundsViscosity: 1.0 // turn off "bounce" when running into max bounds
            } );
            var OSM2 = L.tileLayer( 'https://{s}.tile.osm.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
            } ).addTo( this.leafletMap );

            L.control.scale().addTo( this.leafletMap );

            this.leafletMap.on( 'zoomend', function( event ) {
                self.onZoomEnd( event, xAxisView, yAxisView );
            } );
            this.leafletMap.on( 'moveend', function( event ) { // http://leafletjs.com/reference-1.0.3.html#map-moveend
                self.onMoveEnd( event, xAxisView, yAxisView );
            } );
            // add line at equator
            L.polyline([[0, -180], [0, 180]], {
                alt:"Equator", // or title:, how to get this to show?
                color:"#bbb",
                weight: 1, // stroke thickness
            }).addTo(this.leafletMap)
            // click to show latitude and longitude
            /*
            let map = this.leafletMap;
            this.leafletMap.on('click', function(e){
                var lt = Number(e.latlng.lat).toFixed(5),
                    lg = Number(e.latlng.lng).toFixed(5);
                var popup = L.popup()
                    .setLatLng(e.latlng)
                    .setContent('latitude:'+lt + "\nlongitude:" + lg)
                    .openOn(map);
            });
            */
        }

        // set up axes for a map
        // use only 1 numeric attribute on each axis, no categorical
        this.numAttributesUsedX = (this.isMappingX ? 1 : 0);
        this.numAttributesUsedY = (this.isMappingY ? 1 : 0);
        let wantDisabledLegend = !(this.isMappingX && this.isMappingY);
        let xAxisOptions = {
            isNonLinearMap: true, hideLinesLabels: true, numActiveAttributes: this.numAttributesUsedX,
            isIncompleteAxisStyle: (!this.isMappingX)
        };
        let yAxisOptions = {
            isNonLinearMap: true, hideLinesLabels: true, numActiveAttributes: this.numAttributesUsedY,
            isIncompleteAxisStyle: (!this.isMappingY), marginForButtons: Dyn.plotUtil.mapButtonBottom
        };
        let legendOptions = { disableAttribute: wantDisabledLegend };
        xAxisView.setPlotSpecificOptions( xAxisOptions );
        yAxisView.setPlotSpecificOptions( yAxisOptions );
        subViewRef.rightMargin.setPlotSpecificOptions( legendOptions );
    }

    /**
     * Update the allowable number of case icons show per data case, normally 1.
     * NOTE: here we override the Dyn.DotPlotBase() method.
     */
    calcNumIconsPerCase( ioWhatChanged, subViewRef ) {
        "use strict";
        return 1;
    }

    /**
     * Record which cases that have missing values for the given plot.
     */
    calcMissingValueCases( numCases, xAxisView, yAxisView ) {
        let missingCases = this.plotShaper.dataSet.missingCases;
        const usePreFilterStats = true; // so that filtered-out categories are not marked as missing (they should be marked as filtered only)

        xAxisView.postCalcAxisUpdate(); // update axis type if changed during calcAxis()
        yAxisView.postCalcAxisUpdate();

        // exclude case if any attribute
        for( let caseIndex = 0; caseIndex < numCases; caseIndex++ ) {
            // for each attribute used in x and y
            let isCaseValid = true;
            if( this.isMappingX ) {
                if( !attributeValueIsValid( caseIndex, 0, xAxisView, MapElement.kLngValidRange ) )
                    isCaseValid = false;
            }
            if( this.isMappingY ) {
                if( !attributeValueIsValid( caseIndex, 0, yAxisView, MapElement.kLatValidRange ) )
                    isCaseValid = false;
            }
            missingCases.setCaseIsMissing( caseIndex, !isCaseValid );
        }

        // exclude case if any attribute has a missing value (e.g. blank categorical value, non-numeric on numeric axis)
        function attributeValueIsValid( caseIndex, attributeIndex, axisView, validRange ) {
            var value = axisView.getCaseDataAsNumber( caseIndex, attributeIndex, usePreFilterStats );
            return isFinite( value ) && Dyn.mathUtil.isInRange( value, validRange.min, validRange.max );
        }
    }

    /**
     * Update icons style: use to show a map in the plot area, before calling calcPositions()
     */
    calcPlotSpecificRange( numCases, subViewRef ) {
        //console.log( "Map calcPlotSpecificRange()");
        var xAxisView = subViewRef.xAxisView,
            yAxisView = subViewRef.yAxisView;

        // allow axes and margins to update once based on calcAxis() and calcMissingValueCases() changes
        xAxisView.postCalcAxisUpdate();
        yAxisView.postCalcAxisUpdate();

        // update view coordinates that we want the map to fit within
        let mapViewSizeChanged = this.setDesiredPixBounds( xAxisView, yAxisView );
        if( this.needsFitToData || this.needsRestoreLatLng || this.needThisCenterLatLng || mapViewSizeChanged ) {
            this.setMinZoom(); // dependent on pix bounds
            if( this.needsFitToData && !(this.needsRestoreLatLng || this.needThisCenterLatLng )) {
                // pan+zoom map to fit X-Y attribute data range, within the axes & legend area
                this.fitMapToDataLatLong( xAxisView, yAxisView );
                this.setDesiredLatLngFromMap( xAxisView, yAxisView ); // save desiredLat+desiredLng coords
            } else {
                // pan+zoom map edges to fit desiredLat+desiredLng coords
                this.fitMapToDesiredLatLong( xAxisView, yAxisView );
            }
        }
    }

    /**
     * Update icons style: use to show a map in the plot area, before calling calcPositions()
     */
    calcIconStyle( iconPositions, xAxisView, yAxisView ) {
        const fraction = (this.isMappingX && this.isMappingY) ? Dyn.plotUtil.circleOnMapSizeFraction : 1 / Dyn.plotUtil.circleOnMapSizeFraction;
        this.plotShaper.iconStyler.setStyleCircleOrCustomIcon( iconPositions.length, fraction );
    }

    /**
     * update the case positional data (shared by all elements) and adornments (specific to this element) */
    calcPosition( ioPositions, xAxisView, yAxisView, ioAdornments ) {
        //console.log( "Map calcPosition()");
        // fit map to data or saved map position, in case calcPlotSpecificRange() hasn't handled it
        if( this.needsFitToData ) {
            this.fitMapToDataLatLong( xAxisView, yAxisView );
            this.setDesiredLatLngFromMap( xAxisView, yAxisView );
        } else if( this.needsRestoreLatLng || this.needThisCenterLatLng ){
            this.fitMapToDesiredLatLong( xAxisView, yAxisView );
            this.setDesiredLatLngFromMap( xAxisView, yAxisView );
        }

        // position like a scatterplot, and/or remove dots that are not plottable
        //  by calling DotPlotBase.calcPosition();
        super.calcPosition( ioPositions, xAxisView, yAxisView, ioAdornments );

        if( this.isMappingX || this.isMappingY ) {
            // set icon positions to map, instead of scatterplot axes
            // assumes ioPositions already set by Dyn.DotPlotBase.prototype.calcPosition()
            let iconStyler = this.plotShaper.iconStyler,
                latLngCoords = L.latLng( 0, 0 );
            for( let i = 0, j = ioPositions.length; i < j; i++ ) {
                let p = ioPositions[i];
                if( p.isVisible ) {
                    p = ioPositions[i];
                    latLngCoords.lng = this.isMappingX ? xAxisView.getCaseDataAsNumber( p.caseIndex, p.xAttrIndex ) : xAxisView.getDesiredMinMax().midPoint();
                    latLngCoords.lat = this.isMappingY ? yAxisView.getCaseDataAsNumber( p.caseIndex, p.yAttrIndex ) : yAxisView.getDesiredMinMax().midPoint();
                    let containerPoint = this.leafletMap.latLngToContainerPoint( latLngCoords );
                    // if point offscreen, see if we can offset by 360 to make it visible, since map wraps around in longitude
                    if( containerPoint.x < xAxisView.bounds.left() ) {
                        latLngCoords.lng += 360;  // point offscreen on left, so shift right
                        let testPtR = this.leafletMap.latLngToContainerPoint( latLngCoords );
                        if( testPtR.x <= xAxisView.bounds.right()) {
                            containerPoint = testPtR;
                        }
                    } else if( containerPoint.x > xAxisView.bounds.right() ) {
                        latLngCoords.lng -= 360;  // point offscreen on right, so shift left
                        let testPtL = this.leafletMap.latLngToContainerPoint( latLngCoords );
                        if( testPtL.x >= xAxisView.bounds.left()) {
                            containerPoint = testPtL;
                        }
                    }
                    p.x = containerPoint.x - this.containerToSvgOffsetX; //xAxisView.dataToPixel( p.x );
                    p.y = containerPoint.y - this.containerToSvgOffsetY; //yAxisView.dataToPixel( p.y );
                    // p.r = iconStyler.radius;
                    //console.log( "MapElement.calcPosition( lat "+latLngCoords.lat+" long "+latLngCoords.lng+" x,y: "+containerPoint.x+","+containerPoint.y);
                    if( this.DEBUG_showMarkers && !this.DEBUG_addedMarkers ) {
                        L.marker( latLngCoords ).addTo( this.leafletMap );
                    }
                }
            }
            if( this.DEBUG_showMarkers ) {
                //var thisMap = this.leafletMap;
                //if( !this.DEBUG_addedMarkers ) {
                //    xAxisView.numeric.intervals.values.forEach( function ( xVal ) {
                //        latLngCoords.lng = xVal;
                //        yAxisView.numeric.intervals.values.forEach( function ( yVal ) {
                //            latLngCoords.lat = yVal;
                //            //L.marker( latLngCoords ).addTo( thisMap );
                //            L.circle( latLngCoords, {
                //                color: 'green',
                //                fillColor: '#ffb2e9',
                //                fillOpacity: 0.5,
                //                radius: 0.06
                //            } ).addTo( thisMap );
                //        } );
                //    } );
                //}
                this.DEBUG_addedMarkers = true; // do once only
            }
        }
    }

    /**
     * Update adornments (specific to this element) to show the Drag and Marquee select tools (toggle buttons)
     */
    calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView /*, noAnimation*/ ) {
        const numMapAdornments = 3;

        // create the elements once, if needed
        if( ioAdornArray.length !== numMapAdornments ) {
            Dyn.plotUtil.removeAndEmptyArrayOfElements( ioAdornArray );
            this.dragButton = this.makeToolButton( ioAdornArray, 0, "drag", Dyn.loc( "drag map" ) );
            this.selectButton = this.makeToolButton( ioAdornArray, 1, "select", Dyn.loc( "select cases or annotations" ) );
            this.resetButton = this.makeToolButton( ioAdornArray, 2, "reset", Dyn.loc( "reset view" ) );
            ioAdornArray.push( this.dragButton );
            ioAdornArray.push( this.selectButton );
            ioAdornArray.push( this.resetButton );
        }
        Dyn.assert( ioAdornArray.length === numMapAdornments );

        // THIS is now handled by the plot view, which updates our buttons via setDragMapMode() call when view changes.
        // update button and view state if needed
        //mediator.publish( (this.isInDragMode ? "plot:startViewMode":"plot:stopViewMode"),
        //    { mode:"drag-map", plotElement:this });
    }

    getOutlineStrokeColor( isActiveTool ) {
        return isActiveTool ? Dyn.plotUtil.axisButtonDownColor : Dyn.plotUtil.axisButtonUpColor;
    }

    getSymbolFillColor( isActiveTool ) {
        return isActiveTool ? Dyn.plotUtil.axisButtonDownColor : "black";
    }

    getButtonFillColor( isActiveTool ) {
        return isActiveTool ? Dyn.plotUtil.mapButtonHover : Dyn.plotUtil.mapButtonNormal;
    }

    /**
     * Make a toggle button for the map.
     * @param ioAdornArray
     * @param buttonNum
     * @param buttonTypeString
     * @param toolTipText
     */
    makeToolButton( ioAdornArray, buttonNum, buttonTypeString, toolTipText ) {
        "use strict";

        function buttonHoverIn( e ) {
            e.currentTarget.style.cursor = Dyn.plotUtil.cursorPointer;
            this.attr( { fill: Dyn.plotUtil.mapButtonHover, stroke: Dyn.plotUtil.handleDownColor } );
            const toolTipOffset = 20;
            if( this.data( "tooltip" ) )
                Dyn.toolTip.show( this, event, { x: this.attrs.x + toolTipOffset, y: this.attrs.y + toolTipOffset } );
        }

        function buttonHoverOut( e ) {
            e.currentTarget.style.cursor = Dyn.plotUtil.cursorDefault;
            let isActiveTool =
                    ((this.data( "type" ) === "drag") && plotElement.isInDragMode) ||
                    ((this.data( "type" ) === "select") && !plotElement.isInDragMode),
                fill = plotElement.getButtonFillColor( isActiveTool ),
                stroke = plotElement.getOutlineStrokeColor( isActiveTool );
            this.attr( { fill: fill, stroke: stroke } );
            if( Dyn.toolTip.isMyElement( this ) )
                Dyn.toolTip.hide();
        }

        function buttonClick( /*e*/ ) {
            var thisButton = this,
                buttonType = thisButton.data( "type" );
            switch( buttonType ) {
                case "drag":
                case "select":
                    // tell plot view to enable or disable dragging (and disable marquee-select, etc)
                    mediator.publish(
                        ((buttonType === "drag") ? "plot:startViewMode" : "plot:stopViewMode"),
                        { mode: "drag-map", plotElement: plotElement } );
                    break;
                case "reset":
                    plotElement.resetMap( plotView.xAxis, plotView.yAxis );
            }
        }

        var plotElement = this,
            plotView = Dyn.plotView,
            plotPaper = this.plotShaper.adornments.paper,
            //buttonNum = ((buttonTypeString === "drag") ? 0 :((buttonTypeString === "select") ? 1 : 2)), // drag==0, select==1, reset==2
            isDragTool = buttonTypeString === "drag",
            isSelectTool = buttonTypeString === "select",
            isResetTool = buttonTypeString === "reset",
            isActiveTool = (isDragTool && plotElement.isInDragMode) || (isSelectTool && !plotElement.isInDragMode),
            ariaLabel = toolTipText,
            width = Dyn.plotUtil.mapButtonWidth,
            spacing = Dyn.plotUtil.mapButtonSpacer,
            left = Dyn.plotUtil.mapButtonleft,
            top = Dyn.plotUtil.mapDragButtonTop + (buttonNum * (spacing + width)), // select tool below drag tool
            radius = Dyn.plotUtil.mapButtonRadius,
            set = plotPaper.set(),
            outline = plotPaper.rect( left, top, width, width, radius )
                .attr( {
                    stroke: plotElement.getOutlineStrokeColor( isActiveTool ),
                    fill: plotElement.getButtonFillColor( isActiveTool )
                } )
                .data( "type", buttonTypeString )
                .data( "tooltip", toolTipText )
                .click( buttonClick ),
            path = isDragTool ? Dyn.plotUtil.iconDragHand : (
                isSelectTool ? Dyn.plotUtil.iconMarqueeRectangle :
                    Raphael.transformPath( Dyn.plotUtil.iconResetView, "T-9,-20s0.4,0.4" )),
            transform = isResetTool ? "s1.0,1.0" + "T" + (left - 1) + "," + (top + 10) : "s0.6,0.6" + "T" + (left - 4) + "," + (top - 4),
            symbol = plotPaper.path( Raphael.transformPath( path, transform ) )
                .attr( { stroke: "none", fill: plotElement.getSymbolFillColor( isActiveTool ) } );

        // make element keyboard accessible.
        let keyDownHandler = function( e ) {
            switch( e.key ) {
                case "Enter":
                    if( buttonClick ) {
                        buttonClick.call( outline, e );
                    }
                    break;
            }
        };
        Dyn.plotUtil.setKeyboardFocusable( outline, keyDownHandler, !!ariaLabel ? ariaLabel : false );

        if( !Dyn.environment.isTouchDevice() ) {
            outline.hover( buttonHoverIn, buttonHoverOut );
        }

        // button is a set with an outline and a symbol
        Dyn.plotUtil.disableMouseOverEvents( symbol ); // so it does not interfere with outline's buttonHoverIn()
        set.push( outline ); // [0]
        set.push( symbol );  // [1]
        return set;
    }

    /**
     * Handle a change to zoom level, so plotted points and axes follow the user zoom.
     * Note: use for map('zoomControl':true)
     * Note: leafletMap.fitBounds() also triggers this callback, so we check this.inFitBounds() to avoid endless loop.
     * @param event
     * @param xAxisView
     * @param yAxisView
     */
    onZoomEnd( event, xAxisView, yAxisView ) {
        //console.log( "Map onZoomEnd() map zoom level = " + this.leafletMap.getZoom(), this.inFitBounds ? "Ignored":"Publish" );
        if( !this.inFitBounds ) {   // ignore duplicate calls
            if( this.setDesiredLatLngFromMap( xAxisView, yAxisView ) ) { // save map position
                // if map position changed, update entire plot view (icons, etc)
                mediator.publish( "axis:clickButton", { type: "zoomButton", wantFastCycles:true, noAnimation:true } );
            }
            //else console.log( "MapElement.onZoomEnd has no change to desiredLng, desiredLat", this.desiredLng, this.desiredLat );
        }
    }

    /**
     * Handle a move/drag/pan of the map level, so plotted points and axes follow the drag buttons
     * Note: use with handleAxisButtonClick(), which creates a panTo() call which generates this callback
     * Note: leafletMap.fitBounds() might also trigger this callback, so we check this.inFitBounds() to avoid endless loop.
     * @param event
     * @param xAxisView
     * @param yAxisView
     */
    onMoveEnd( event, xAxisView, yAxisView ) {
        //console.log( "Map onMoveEnd()", this.inFitBounds ? "Ignored":"Publish" );
        if( !this.inFitBounds ) {  // ignore duplicate calls
            if( this.setDesiredLatLngFromMap( xAxisView, yAxisView ) ) { // save map position
                // if map position changed, update entire plot view (icons, etc)
                mediator.publish( "axis:clickButton", { type: "dragToPan", wantFastCycles:true, noAnimation:true } );
            }
            //else console.log( "MapElement.onMoveEnd has no change to desiredLng, desiredLat", this.desiredLng, this.desiredLat );
        }
    }

    /**
     * Alows each element to respond to an axis button click
     * @param ioWhatChanged -- element should add a Dyn.EPlotChange parameter if returning true.
     * @param subViewRef
     * @param axisdata
     * @returns {boolean} -- true if handled by a Dyn.PlotElement
     */
    handleAxisButtonClick( ioWhatChanged, subViewRef, axisData ) {
        var handled = false;
        switch( axisData.type ) {
            case "zoomButton":
            case "dragToPan":
            case "resetButton":
                // the map's zoom, pan button has been pressed, or our own reset button has been pressed,
                // if we get this notification, it means that this.desiredLat or this.desiredLng have changed
                // and the plot icons and adornments (e.g. mean line) need to move.
                // we need to tell it what to update....
                //ioWhatChanged.viewSize = true; // update axes, so this.CalcPlotSpecificRange() is called
                ioWhatChanged.iconPosition = true; // update view, so this.calcPosition() is called
                ioWhatChanged.wantFastCycles = true; // fast animation response to buttons
                //ioWhatChanged.noAnimation = true; // user can use Gear Menu > Animation > Off instead
                handled = true;
                break;
        }
        return handled;
    }

    /**
     * Set the view boundary that we want the map to plot our desired latitude and longitude range within.
     * Map will cover the entire plot view, but we want plotted points within the axes and margins.
     * @param xAxisView
     * @param yAxisView
     * @returns {Boolean} - true if desiredPixBounds have changed
     */
    setDesiredPixBounds( xAxisView, yAxisView ) {
        var oldRect = new Dyn.mathUtil.Rect().copyRect( this.desiredPixBounds );
        // update view coordinates that we want the map to fit within
        this.desiredPixBounds = new Dyn.mathUtil.Rect(
            xAxisView.bounds.x,
            yAxisView.bounds.y,
            Math.round( xAxisView.bounds.width ),
            Math.round( yAxisView.bounds.height ) );
        //console.log( "Map setDesiredPixBounds()", (oldRect.equals( this.desiredPixBounds ))?"unchanged":"CHANGED", this.desiredPixBounds, oldRect);
        return (!oldRect.equals( this.desiredPixBounds ));
    }

    /**
     * set the desired longitude (X) and latitude (Y) that the map should cover,
     * From the axis range (which is in turn based on the data range)
     * @param xAxisView
     * @param yAxisView
     */
    fitMapToDataLatLong( xAxisView, yAxisView ) {
        // get the desired longitude (X) and latitude (Y) that the map should cover
        if( xAxisView.isNumericAxis() &&
            xAxisView.allAttributes.stats.min >= MapElement.kLngValidRange.min &&
            xAxisView.allAttributes.stats.max <= MapElement.kLngValidRange.max ) {

            this.desiredLng = xAxisView.getDesiredMinMax();
        } else {
            //  default to world coordinates if values are non-numeric or out of range or all missing
            this.desiredLng = new Dyn.mathUtil.Range( MapElement.kWorldLng.min, MapElement.kWorldLng.max );
        }
        if( yAxisView.isNumericAxis() &&
            yAxisView.allAttributes.stats.min >= MapElement.kLatValidRange.min &&
            yAxisView.allAttributes.stats.max <= MapElement.kLatValidRange.max ) {

            this.desiredLat = yAxisView.getDesiredMinMax();
        } else {
            //  default to world coordinates if values are non-numeric or out of range or all missing
            this.desiredLat = new Dyn.mathUtil.Range( MapElement.kWorldLat.min, MapElement.kWorldLat.max );
        }
        //console.log("MapElement.fitMapToDataLatLong Lng,Lat", this.desiredLng, this.desiredLat );

        // now change the map to fit the desired coordinates within the axes (avoid legend, etc)
        let mapContainerSize = this.leafletMap.getSize(),
            margins = {
                left: Math.round( this.desiredPixBounds.left() ),
                right: Math.round( Math.max( 0, mapContainerSize.x - this.containerToSvgOffsetX - this.desiredPixBounds.right() )),
                top: Math.round( this.desiredPixBounds.top() ),
                bottom: Math.round( Math.max( 0, mapContainerSize.y - this.containerToSvgOffsetY - this.desiredPixBounds.bottom() ) )
            };
        this.fitMapToDesiredLatLong( xAxisView, yAxisView, margins );
        this.needsFitToData = false;
    }

    /**
     * Position or re-position the map, to the coordinate range of the axes.
     * Note that the fit will only be approximate, because of integer zoom levels.
     * @param xAxisView
     * @param yAxisView
     * @param [margins]
     */
    fitMapToDesiredLatLong( xAxisView, yAxisView, margins= { left:0, top:0, right:0, bottom:0 } ) {
        //console.log("MapElement.fitMapToDesiredLatLong Lng,Lat", this.desiredLng, this.desiredLat, margins, this.leafletMap.getZoom() );
        this.inFitBounds = true;
        if( this.needThisCenterLatLng && ! this.needsRestoreLatLng ) {
            // pan the mop to the given center position; used for restore of old plots only
            this.leafletMap.panTo( this.needThisCenterLatLng );
        } else {
            // request the map cover the desired latitude and longitude
            this.leafletMap.fitBounds( // http://leafletjs.com/reference-1.0.3.html#map-fitbounds
                [   // Note: because of fixed zoom levels the map will not fit view or lat/lng exactly
                    [ this.desiredLat.max, this.desiredLng.min ], // north,west -> top, left
                    [ this.desiredLat.min, this.desiredLng.max ]  // south,east -> bottom, right
                ],
                {   // padding to place the map within the axes/legend margins?
                    paddingTopLeft: [ margins.left, margins.top ],
                    paddingBottomRight: [ margins.right, margins.bottom ]
                }
            );
        }
        this.inFitBounds = false;
        // clear our fitMap flags
        this.needsFitToData = false;
        delete this.needsRestoreLatLng;
        delete this.needThisCenterLatLng;
    }

    /**
     * Move the axes endponts to match the map.
     * The map cannot exactly go to the desired ranges, it has fixed (integer) zoom levels.
     * @param xAxisView
     * @param yAxisView
     * @returns {Boolean} - true if axes have changed
     */
    setDesiredLatLngFromMap( xAxisView, yAxisView ) {
        let latLng = this.leafletMap.getBounds(),
            mapLat = new Dyn.mathUtil.Range( latLng.getSouth(), latLng.getNorth() ),   // y-axis / vertical
            mapLng = new Dyn.mathUtil.Range( latLng.getWest(), latLng.getEast() ),     // x-axis / horizontal
            changed = !( this.desiredLat.equals( mapLat ) && this.desiredLng.equals( mapLng ));

        // update the desired latitude and longitude
        this.desiredLat = mapLat;
        this.desiredLng = mapLng;
        // update the axis to match
        //  NOTE: we don't use the axis range yet, it just pasively tracks desiredLat, desiredLng for now
        xAxisView.setPlotSpecificMinMax( mapLng );
        yAxisView.setPlotSpecificMinMax( mapLat );

        //console.log("MapElement.setDesiredLatLngFromMap Lng,Lat", this.desiredLng, this.desiredLat );
        //console.log( "X: Desired Long "+this.desiredLng.min+" to "+this.desiredLng.max );
        //console.log( "X:     Map Long "+latLng.getWest()+" to "+latLng.getEast());
        //console.log( "X:    Axis Long "+xAxisView.getDesiredMinMax()); //numeric.plotRange );
        //console.log( "Y: Desired Lat  "+this.desiredLat.min+" to "+this.desiredLat.max );
        //console.log( "Y:     Map Lat  "+latLng.getSouth()+" to "+latLng.getNorth());
        //console.log( "Y:    Axis Lat  "+yAxisView.getDesiredMinMax()); //numeric.plotRange );
        //console.log("Map zoom level = " + this.leafletMap.getZoom() );
        //console.log("Map center Lat, Lng: ", this.desiredLat.midPoint(), this.desiredLng.midPoint() );
        return changed;
    }

    /**
     * Respond to a change in the plot view mode, so that clicks on the map either drag or select.
     * Called by PlotView.startViewMode() after this or another plot send notification
     * that the view mode has changed, so our map behavior and button visuals are coordinated.
     * See "plot:startViewMode" and "plot:stopViewMode" notifications.
     * @param wantDragMapMode
     */
    setDragMapMode( wantDragMapMode ) {
        // drag mode and select mode are mutually exclusive modes
        this.isInDragMode = Boolean( wantDragMapMode ); // true===drag mode, false===marquee select mode

        // Tell LeafletJS map to enable or disable dragging
        //  Because Map layer only handles dragging
        if( this.isInDragMode )
            this.leafletMap.dragging.enable();
        else
            this.leafletMap.dragging.disable();

        // update button visuals
        if( this.dragButton ) {
            this.dragButton[0].attr( {
                fill: this.getButtonFillColor( wantDragMapMode ),
                stroke: this.getOutlineStrokeColor( wantDragMapMode )
            } );
            this.dragButton[1].attr( {
                fill: this.getSymbolFillColor( wantDragMapMode )
            } );
            this.selectButton[0].attr( {
                fill: this.getButtonFillColor( !wantDragMapMode ),
                stroke: this.getOutlineStrokeColor( !wantDragMapMode )
            } );
            this.selectButton[1].attr( {
                fill: this.getSymbolFillColor( !wantDragMapMode )
            } );
        }
    }

    /**
     * Set the minimim zoom, appropriate for a world map at the current plot view size.
     */
    setMinZoom() {
        let viewHeight = this.desiredPixBounds.height,
            viewWidth = this.desiredPixBounds.width;
        if( viewHeight > 0 && this.leafletMap !== null ) {
            // computer the minimum zoom level so that
            //  (1) minZoomH : the user cannot zoom out to see white borders above the North Pole, or below the South Pole
            //  (2) minZoomV :
            // The leaflet tiles at a particular zoom level have a fixed pixel size; at zoom level 0, the world is mapped to 256x256 pixels.
            // See https://leafletjs.com/examples/zoom-levels/
            const leafletTileSize = 256; // at zoom level 0: -90 to 90 latitude is 256 pixels, 1: 512, 2: 1024, 3: 2048, etc.
            let minZoomH = Dyn.mathUtil.floor( Math.log2( viewHeight / leafletTileSize )/MapElement.kZoomSnap )*MapElement.kZoomSnap - MapElement.kZoomSnap,
                minZoomV = Dyn.mathUtil.floor( Math.log2( viewWidth / leafletTileSize )/MapElement.kZoomSnap )*MapElement.kZoomSnap  - MapElement.kZoomSnap;
            this.leafletMap.setMinZoom( Math.max(0, Math.min( minZoomH, minZoomV )));
            //console.log("Map setMinZoom()", minZoomH, minZoomV, "view height = "+viewHeight, "view width = "+viewWidth);
        }
    }

    /**
     * reset the map zoom and pan to show all the data points, or if not mapping, to the default view.
     */
    resetMap( xAxisView, yAxisView ) {
        //console.log("Map reset map");
        // clear any user-specified and plot-specified axis settings
        // so that the axis range defaults to the attribute data range
        xAxisView.setPlotSpecificMinMax();
        yAxisView.setPlotSpecificMinMax();
        xAxisView.setUserCustomizedMinMax();
        yAxisView.setUserCustomizedMinMax();

        // update map placement to match axes (view all data points, or if no data, to see world map)
        this.fitMapToDataLatLong( xAxisView, yAxisView );
        if( this.setDesiredLatLngFromMap( xAxisView, yAxisView ) ) { // save map position
            // if map position changed, update entire plot view (icons, etc)
            mediator.publish( "axis:clickButton", { type: "resetButton", wantFastCycles:true, noAnimation:true } );
        }
        //else console.log( "MapElement.resetMap has no change to desiredLng, desiredLat", this.desiredLng, this.desiredLat );
    }

    /**
     * Tell plot elements to hide or show any controls.
     */
    setViewMode( mode ) {
        // NOTE: buttons may be null, that is OK
        let showControls = mode.showControls();
        Dyn.plotUtil.showOrHideElement( this.dragButton, showControls );
        Dyn.plotUtil.showOrHideElement( this.selectButton, showControls );
        Dyn.plotUtil.showOrHideElement( this.resetButton, showControls );
        if( this.leafletMap ) {
            if( showControls ) {
                this.leafletMap.addControl( this.leafletMap.zoomControl );
            } else {
                this.leafletMap.removeControl( this.leafletMap.zoomControl );
            }
        }
    }

    /**
     * Temporary disable drag mode to prepare for dragging of a plot element
     * that interferes with drag mode. For issue #1532, #1531.
     * Note: the normal way to do this would be to call event.stopPropogation()
     * which apparently does not work with leaflet, so we use this workaround.
     * See also setDragMapMode()
     * @param isDragStart
     */
    prepareElementsForDrag( isDragStart ) {
        this.tempDisableDragMode = (isDragStart && this.isInDragMode);
        if( this.tempDisableDragMode ) {
            this.leafletMap.dragging.disable();
            //console.log("MapElement.preparePlotForDrag temporary disable drag map mode");
        } else if( this.isInDragMode ) {
            this.leafletMap.dragging.enable();
            //console.log("MapElement.preparePlotForDrag re-enable drag map mode");
        }
    }

    /** Do we want the plot to show a context menu in the plot title? */
    contextMenuOptions() {
        return {
            wantContextMenu:true,
            menuID: Dyn.plotUtil.mapPlotContextMenuID,
            menuToolTip: 'map plot menu'
        };
    }

    /** Get the static context menu handler  */
    getContextMenuClickHandler() {
        return MapElement.handleContextMenuClick;
    }

    /**
     * Handle a click on the plot title's context menu.
     * @param event -- keyboard or mouse event
     * @param buttonElement -- the SVG element that invoked the event
     * @param plotElement
     */
    static handleContextMenuClick( event, buttonElement, plotElement ) {
        let xy = Dyn.plotUtil.clickOrKeyboardEventToViewportXY( event, buttonElement );
        plotElement = null; // forget reference; plotElement will be removed from plot if axisType change
        setTimeout( () => { // delayed call is required to show when e.type==='keydown', reason unknown
            ContextMenuUtil.show( {x:xy.x, y:xy.y}, {
                    // HistoGramContextMenu component props
                    getMapState: function( mapElement ) { return {
                        plotType: mapElement.getMapPlotType(),
                        // binOptions: histoElement.getHistBinOptions()
                    }}
                },
                ( code ) => {
                    // HistoGramContextMenu.props.handleChange() callback
                    if( code && code.change==="plotType" ) {
                        MapElement.setMapPlotType( code.plotType, Dyn );
                    }
                }, Dyn.plotUtil.mapPlotContextMenuID, Dyn );
        }, 100 );
    }

    getMapPlotType() {
        return this.primaryType;
    }

    static setMapPlotType( mapType ) {
        console.log( "MapElement.setMapPlotType", mapType );
    }

} // class MapElement

Dyn.MapElement = MapElement;
//Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsMap, Dyn.MapElement );

    /**
     * Dyn.MapOnlyElement()
     * A map plot with no bubbles or other adornments.
     * This class is identical to MapElement, but is used to distinguish between a map plot and a map+bubble plot.
     */
    class MapOnlyElement extends MapElement {

        constructor() {
            super( Dyn );
            this.initElement( {
                priority: Dyn.EPlotPriority.kPriorityShape,
                replaceType: Dyn.EPlotControl.kControlsShape,
                primaryType: Dyn.EPlotControl.kControlsMapOnly, // custom primary type is used to distinguish from map+bubble plot
                otherControls: []
            }, Dyn );
        }
    } // class MapOnlyElement

    Dyn.MapOnlyElement = MapOnlyElement;
    Dyn.PlotElemHelper.registerOldType( Dyn.EPlotControl.kControlsMap, (savedData) => Dyn.MapOnlyElement ); // old Map plot creates MapOnlyElement
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsMapOnly, Dyn.MapOnlyElement );

    /**
     * Dyn.MapAndBubbleElement()
     */
    class MapAndBubbleElement extends MapElement {

        constructor() {
            super( Dyn );
            this.initElement( {
                priority: Dyn.EPlotPriority.kPriorityShape,
                replaceType: Dyn.EPlotControl.kControlsShape,
                primaryType: Dyn.EPlotControl.kControlsMapAndBubble,
                otherControls: [
                    Dyn.EPlotControl.kControlsBubbles, // so that getBubbleRadius() is called
                    Dyn.EPlotControl.kControlsCaseOrder, // so case order attribute is used for bubble size
                ]
            }, Dyn );
            this.bubbleHelper = new BubbleHelper( Dyn, this );
        }

        get kDefaultTitle() {
            return Dyn.plotTitles.MAP_AND_BUBBLE_PLOT;
        }

        get kUndoAction() {
            return Dyn.plotTitles.MAP_AND_BUBBLE_PLOT;
        }

        displayOrderBy() {
            return true; // show the attribute controlling the bubble value (saved as the sort attribute)
        }

        allowCustomOrderBy() {
            return true; // allow user changes to the attribute controlling the bubble value (saved as the sort attribute)
        }

        getBubbleAttribute() {
            return this.bubbleHelper.getBubbleAttribute();
        }

        hasValidBubbleAttribute() {
            return this.bubbleHelper.hasValidBubbleAttribute();
        }

        handleAxisButtonClick( ioWhatChanged, subViewRef, axisData ) {
            // handle the plus/minus buttons on the legend, which change the bubble size
            let handled = this.bubbleHelper.handleAxisButtonClick( ioWhatChanged, subViewRef, axisData );
            if( !handled ) {
                // else handle the zoom, pan, or reset buttons on the map
                handled = super.handleAxisButtonClick( ioWhatChanged, subViewRef, axisData );
            }
            return handled;
        }

        calcIconStyle( iconPositions, xAxisView, yAxisView ) {
            const fraction = (this.isMappingX && this.isMappingY) ? Dyn.plotUtil.bubbleOnMapSizeFraction : 1 / Dyn.plotUtil.circleOnMapSizeFraction;
            this.plotShaper.iconStyler.setStyleCircleOrCustomIcon( iconPositions.length, fraction );
        }

        calcAxis( ioWhatChanged, subViewRef ) {
            super.calcAxis( ioWhatChanged, subViewRef );
            this.isIncompletePlot = !(this.isMappingX && this.isMappingY);
            let legendPlotSpecificOptions = this.bubbleHelper.legendPlotSpecificOptions( this.isIncompletePlot );
            subViewRef.rightMargin.setPlotSpecificOptions( legendPlotSpecificOptions );
        }

    } // class MapAndBubbleElement

    Dyn.MapAndBubbleElement = MapAndBubbleElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsMapAndBubble, Dyn.MapAndBubbleElement );


} // wrapDynMapElement
